/*
 * Commission earned per month, the last 12 months: a hand-rolled SVG bar
 * chart in the spirit of the client app's EquityChart. The SVG is drawn at
 * the container's real pixel width (ResizeObserver), so bars and labels stay
 * crisp rather than being stretched by preserveAspectRatio="none". Hover, or
 * focus and use the arrow keys, to read a month.
 */

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Money } from "../api/client";
import { fmtMonth, inr, inrCompact } from "./format";

const HEIGHT = 200;
const PAD = { top: 12, right: 4, bottom: 26, left: 44 };

/** A round top for the axis: 1, 2, 2.5 or 5 × a power of ten. */
function niceMax(v: number): number {
  if (v <= 0) return 1000;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (v <= m * p) return m * p;
  return 10 * p;
}

export default function EarningsChart({ months }: { months: { month: string; amount: Money }[] }) {
  const boxRef = useRef<HTMLDivElement>(null);
  // 0 until measured: the SVG is only drawn at the box's real width, so it
  // can never push a phone-width layout wider than the screen.
  const [width, setWidth] = useState(0);
  const [active, setActive] = useState<number | null>(null);

  useLayoutEffect(() => {
    const node = boxRef.current;
    if (!node) return;
    const measure = (w: number) => setWidth(Math.max(240, Math.floor(w)));
    measure(node.getBoundingClientRect().width);
    const ro = new ResizeObserver(([entry]) => measure(entry.contentRect.width));
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const values = useMemo(() => months.map((m) => Number(m.amount) || 0), [months]);
  const top = niceMax(Math.max(...values, 0));
  const plotW = width - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const slot = plotW / Math.max(1, months.length);
  const barW = Math.max(6, Math.min(34, slot * 0.56));
  const y = (v: number) => PAD.top + plotH - (v / top) * plotH;
  // Every month label when there is room; every other one on a phone.
  const labelEvery = slot < 34 ? 2 : 1;
  const ticks = [0, top / 2, top];
  const total = values.reduce((s, v) => s + v, 0);

  const last = months.length - 1;
  const shown = active ?? null;

  return (
    <div className="earn-chart">
      <div
        ref={boxRef}
        className="earn-chart__box"
        role="group"
        tabIndex={0}
        aria-label={`Commission earned per month for the last ${months.length} months, ${inr(total)} in total. Use the arrow keys to read each month.`}
        onPointerLeave={() => setActive(null)}
        onBlur={() => setActive(null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            e.preventDefault();
            setActive((a) => Math.max(0, Math.min(last, (a ?? last) + (e.key === "ArrowRight" ? 1 : -1))));
          } else if (e.key === "Home") setActive(0);
          else if (e.key === "End") setActive(last);
          else if (e.key === "Escape") setActive(null);
        }}
      >
        {width > 0 && (
        <svg width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} aria-hidden="true">
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(t)}
                y2={y(t)}
                className={t === 0 ? "earn-chart__base" : "earn-chart__grid"}
              />
              <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="earn-chart__tick">
                {inrCompact(t)}
              </text>
            </g>
          ))}
          {months.map((m, i) => {
            const v = values[i];
            const cx = PAD.left + slot * i + slot / 2;
            const h = Math.max(v > 0 ? 2 : 0, (v / top) * plotH);
            return (
              <g key={m.month} onPointerEnter={() => setActive(i)}>
                {/* The whole column is the hover target, not just the bar. */}
                <rect x={cx - slot / 2} y={PAD.top} width={slot} height={plotH} fill="transparent" />
                {v > 0 ? (
                  <rect
                    className={`earn-chart__bar${shown === i ? " is-active" : ""}${i === last ? " is-current" : ""}`}
                    style={{ animationDelay: `${i * 25}ms` }}
                    x={cx - barW / 2}
                    y={y(0) - h}
                    width={barW}
                    height={h}
                    rx={Math.min(4, barW / 4)}
                  />
                ) : (
                  <rect className="earn-chart__zero" x={cx - barW / 2} y={y(0) - 1} width={barW} height={1} />
                )}
                {(i % labelEvery === (last % labelEvery) || shown === i) && (
                  <text x={cx} y={HEIGHT - 8} textAnchor="middle" className={`earn-chart__label${shown === i ? " is-active" : ""}`}>
                    {fmtMonth(m.month)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        )}

        {shown != null && months[shown] && (
          <div
            className="earn-chart__tip"
            role="status"
            style={{
              left: `${Math.min(width - 90, Math.max(90, PAD.left + slot * shown + slot / 2))}px`,
              top: `${Math.max(0, y(values[shown]) - 58)}px`,
            }}
          >
            <strong>{fmtMonth(months[shown].month, "long")}</strong>
            <span>{inr(months[shown].amount)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
